import { useCallback, useMemo, useState } from 'react';

import { Slider } from '@material-ui/core';

import classNames from 'classnames';

import { formatPlaybackTime } from '../../../helpers';
import type { PageSegment } from './types';

import styles from './TimelineSlider.module.css';

type SegmentCluster = {
  startPercent: number;
  segments: PageSegment[];
};

const CLUSTER_THRESHOLD_PERCENT = 2;

const groupSegmentsIntoClusters = (
  segments: PageSegment[],
): SegmentCluster[] => {
  const withoutFirst = segments.slice(1);
  if (withoutFirst.length === 0) return [];

  const clusters: SegmentCluster[] = [];
  let current: SegmentCluster = {
    startPercent: withoutFirst[0].startPercent,
    segments: [withoutFirst[0]],
  };

  for (let i = 1; i < withoutFirst.length; i++) {
    const segment = withoutFirst[i];
    const lastInCluster = current.segments[current.segments.length - 1];
    if (
      segment.startPercent - lastInCluster.startPercent <=
      CLUSTER_THRESHOLD_PERCENT
    ) {
      current.segments.push(segment);
    } else {
      clusters.push(current);
      current = { startPercent: segment.startPercent, segments: [segment] };
    }
  }
  clusters.push(current);
  return clusters;
};

type TimelineSliderProps = {
  value: number;
  max: number;
  onChange: (_: unknown, value: number | number[]) => void;
  pageSegments?: PageSegment[];
};

export const TimelineSlider = ({
  value,
  max,
  onChange,
  pageSegments,
}: TimelineSliderProps) => {
  const [hoverPercent, setHoverPercent] = useState<number | null>(null);

  const clusters = useMemo(
    () => (pageSegments ? groupSegmentsIntoClusters(pageSegments) : []),
    [pageSegments],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (max <= 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      setHoverPercent(
        Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      );
    },
    [max],
  );

  const handleMouseLeave = useCallback(() => setHoverPercent(null), []);

  const hoveredSegment = useMemo(
    () =>
      hoverPercent !== null && pageSegments
        ? pageSegments.findLast(s => s.startPercent <= hoverPercent * 100)
        : undefined,
    [hoverPercent, pageSegments],
  );

  const hoveredCluster = useMemo(
    () =>
      hoverPercent !== null
        ? clusters.find(c => {
            const first = c.segments[0].startPercent;
            const last = c.segments[c.segments.length - 1].startPercent;
            const percent = hoverPercent * 100;
            return (
              c.segments.length > 1 &&
              percent >= first - 1 &&
              percent <= last + 1
            );
          })
        : undefined,
    [hoverPercent, clusters],
  );

  return (
    <div
      className={styles.root}
      data-testid="timeline-hover-area"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {hoverPercent !== null && (
        <div
          className={styles.hoverLabel}
          style={{
            left: `${hoverPercent * 100}%`,
            transform: `translateX(-${hoverPercent * 100}%)`,
          }}
        >
          {hoveredCluster ? (
            <div className={styles.hoverClusterList}>
              {hoveredCluster.segments.map((segment, i) => (
                <span key={i} className={styles.hoverRoute}>
                  <span className={styles.hoverRouteTime}>
                    {formatPlaybackTime(segment.timestampMs)}
                  </span>
                  {segment.url}
                </span>
              ))}
            </div>
          ) : (
            <>
              {hoveredSegment && (
                <>
                  <span className={styles.hoverRoute}>
                    {hoveredSegment.url}
                  </span>
                  {' · '}
                </>
              )}
              {formatPlaybackTime(hoverPercent * max)}
            </>
          )}
        </div>
      )}
      {clusters.map(cluster => (
        <div
          key={cluster.startPercent}
          className={classNames(
            styles.segmentDivider,
            cluster.segments.length > 1 && styles.segmentCluster,
          )}
          style={{ left: `${cluster.startPercent}%` }}
        />
      ))}
      <Slider
        className={styles.timeline}
        classes={{
          rail: styles.rail,
          track: styles.track,
          thumb: styles.thumb,
        }}
        value={value}
        min={0}
        max={max}
        onChange={onChange}
        aria-label="Playback timeline"
        data-track-id="slider.playback-timeline"
        data-track-action="seek"
      />
    </div>
  );
};
